/**
 * Custom `regexMatches` operator.
 *
 * Compares a string fact against a regex pattern provided in the rule JSON.
 * Used by the ad-review lint (D-22 / D-32) to flag copy/imagery that violates
 * the rules in `@/Users/karlbernard/workspace/nextday-onboarding/docs/AD_REVIEW_LINT.md`.
 *
 * Rule JSON shape:
 *   { fact: "headline", operator: "regexMatches", value: { pattern: "free|guaranteed", flags: "i" } }
 *
 * Either `value` is a `{ pattern, flags? }` object, or a bare regex source string.
 */

type RegexMatchesValue = string | { pattern: string; flags?: string };

const isRegexValue = (candidate: unknown): candidate is RegexMatchesValue => {
  if (typeof candidate === "string") return true;
  if (typeof candidate !== "object" || candidate === null) return false;
  const record = candidate as Record<string, unknown>;
  return typeof record.pattern === "string";
};

const toRegExp = (value: RegexMatchesValue): RegExp => {
  if (typeof value === "string") return new RegExp(value);
  return new RegExp(value.pattern, value.flags ?? "");
};

/** json-rules-engine operator callback: `(factValue, ruleValue) => boolean`. */
const regexMatchesEvaluator = (factValue: unknown, ruleValue: unknown): boolean => {
  if (typeof factValue !== "string") return false;
  if (!isRegexValue(ruleValue)) return false;
  return toRegExp(ruleValue).test(factValue);
};

/**
 * Tuple consumed by `Engine.addOperator(name, callback)`.
 * Spread it: `engine.addOperator(...regexMatchesOperator)`.
 */
const regexMatchesOperator = ["regexMatches", regexMatchesEvaluator] as const;

export default regexMatchesOperator;
